/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import {
    CacheType,
    createObservableDataAction,
    IAction,
    IActionContext,
    IActionInput,
    IAny,
    ICreateActionContext,
    IGeneric
} from '@msdyn365-commerce/core';
import {
    DeliveryModeTypeFilter,
    OrgUnitAvailability,
    OrgUnitLocation,
    ItemAvailability,
    ProductWarehouseInventoryInformation,
    SearchArea
} from '@msdyn365-commerce/retail-proxy';
import {
    getOrgUnitLocationsByAreaAsync
} from '@msdyn365-commerce/retail-proxy/dist/DataActions/OrgUnitsDataActions.g';
import { getEstimatedAvailabilityAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';

import {
    GenericInput,
    DeliveryMode,
    createInventoryAvailabilitySearchCriteria,
    mapProductInventoryInformation,
    QueryResultSettingsProxy
} from '@msdyn365-commerce-modules/retail-actions';
import { getSelectedProductIdFromActionInput } from '../../../utilities/utils';
import getProductDefaultVariantAction, { createProductDefaultVariantInput } from '../../../actions/get-product-default-variant.action';
//mmx-feature --start
/*
import { storeSelectorStateDataAction } from '@msdyn365-commerce-modules/bopis-utilities';
*/
import storeSelectorStateDataAction from '../../mmx-store-selector/state-manager/store-selector-state-manager-data-action';
//mmx-feature --end
import { IStoreSelectorStateManager } from '../../mmx-store-selector/state-manager';
import { MapStoreSelectorStateManager } from '../../mmx-store-selector/state-manager/map-store-selector-state-manager';

/**
 * Get selected variant action input class.
 */
export class GetAvailableInventoryNearbyInput implements IActionInput {
    public latitude?: number;

    public longitude?: number;

    public radius?: number;

    public productId?: number;

    public DistanceUnitValue?: number;

    public IgnoreLocation?: boolean;

    constructor(
        _productId?: number,
        _latitude?: number,
        _longitude?: number,
        _radius?: number,
        _DistanceUnitValue?: number,
        _IgnoreLocation?: boolean
    ) {
        this.productId = _productId;
        this.latitude = _latitude;
        this.longitude = _longitude;
        this.radius = _radius;
        this.DistanceUnitValue = _DistanceUnitValue;
        this.IgnoreLocation = _IgnoreLocation;
    }

    public getCacheKey = () => 'GetAvailableInventoryNearby';

    public getCacheObjectType = () => 'GetAvailableInventoryNearbyInput';

    public dataCacheType = (): CacheType => 'none';
}

/**
 * CreateInput method for the getSelectedVariant data action.
 * @param inputData - The input data passed to the createInput method.
 * @returns GetAvailableInventoryNearbyInput - The action input.
 */
export const createGetAvailableInventoryNearbyInput = (
    inputData: ICreateActionContext<IGeneric<IAny>>
): GetAvailableInventoryNearbyInput => {
    const selectedId = getSelectedProductIdFromActionInput(inputData);
    if (selectedId) {
        return new GetAvailableInventoryNearbyInput(+selectedId);
    }
    throw new Error('Unable to find product warranties, no productId found on module config or query');
};

/**
 * Action method for the getSelectedVariant data aciton.
 * @param input - The action input class.
 * @param ctx - The action context.
 * @returns IFullOrgUnitAvailability - The org unit availability.
 */
export async function getAvailableInventoryNearbyAction(
    input: GetAvailableInventoryNearbyInput,
    ctx: IActionContext
): Promise<OrgUnitAvailability[]> {
    const [product, storeSelectorStateManager] = await Promise.all([
        getProductDefaultVariantAction(
            createProductDefaultVariantInput({
                requestContext: ctx.requestContext,
                config: {
                    productId: input.productId
                }
            }),
            ctx
        ).then(vars => {
            return vars && vars.length > 0 ? vars[0] : undefined;
        }),
        storeSelectorStateDataAction(
            new GenericInput<IStoreSelectorStateManager>('mmx_mapSSSM', new MapStoreSelectorStateManager(), 'IStoreSelectorStateManager'),
            ctx
        ).catch((error: Error) => {
            ctx.trace(
                '[getAvailableInventoryNearbyAction] Error finding store selector state manager'
            );
            ctx.telemetry.debug(
                '[getAvailableInventoryNearbyAction] Error finding store selector state manager'
            );
            throw new Error('Error finding store selector state manager');
        })
    ]);

    // No valid product we want to return undefined so module knows there are no results yet
    if (!product) {
        ctx.trace(
            '[GetAvailableInventoryNearby] No product was provided for inventory search'
        );
        ctx.telemetry.debug(
            '[GetAvailableInventoryNearby] No product was provided for inventory search'
        );
        return [];
    }

    const searchArea: SearchArea = {
        Latitude: storeSelectorStateManager?.preferredStore?.Latitude,
        Longitude: storeSelectorStateManager?.preferredStore?.Longitude,
        Radius: 0, //gets all products
        DistanceUnitValue: input.DistanceUnitValue || 0 // 0 is miles
    };

    const searchCriteria = createInventoryAvailabilitySearchCriteria(ctx, [product.DefaultProductId], false, true, searchArea, DeliveryMode.pickup);
    return getEstimatedAvailabilityAsync({ callerContext: ctx }, searchCriteria)
        .then(async (productWarehouseInformation: ProductWarehouseInventoryInformation) => {
            // For store selector, inventory should always come from an individual store
            const productInventoryInformation = mapProductInventoryInformation(
                ctx,
                productWarehouseInformation.ProductWarehouseInventoryAvailabilities
            );
            return getOrgUnitLocationsByAreaAsync(
                {
                    callerContext: ctx,
                    queryResultSettings: QueryResultSettingsProxy.getPagingFromInputDataOrDefaultValue(ctx)
                },
                searchArea,
                DeliveryModeTypeFilter.Pickup
            )
                .then(async (stores: OrgUnitLocation[]) => {
                    const availabilityPromiseList: OrgUnitAvailability[] = stores.map(store => {
                        const itemAvailabilities : ItemAvailability[] = productInventoryInformation
                            .filter(pii => { return pii.InventLocationId === store.InventoryLocationId; })
                            .map(pii => { return { AvailableQuantity: pii.ProductAvailableQuantity.AvailableQuantity }; });

                        return {
                            OrgUnitLocation: store,
                            ItemAvailabilities: itemAvailabilities
                        };
                    });
                    return availabilityPromiseList;
                })
                .catch((error: Error) => {
                    ctx.trace('[GetAvailableInventoryNearby][getOrgUnitLocationsByAreaAsync] error getting Available Inventory Nearby');
                    ctx.trace(error.message);
                    ctx.telemetry.error(error.message);
                    ctx.telemetry.debug('[GetAvailableInventoryNearby][getOrgUnitLocationsByAreaAsync] error getting Available Inventory Nearby');
                    return [];
                });
        })
        .catch((error: Error) => {
            ctx.trace(
                '[GetAvailableInventoryNearby][getEstimatedAvailabilityAsync] error getting availability product warehouse information.'
            );
            ctx.trace(error.message);
            ctx.telemetry.error(error.message);
            ctx.telemetry.debug(
                '[GetAvailableInventoryNearby][getEstimatedAvailabilityAsync] error getting availability product warehouse information.'
            );
            return [];
        });
}

/**
 * The complete getAvailableInventoryNearby data action.
 */
export const getAvailableInventoryNearbyActionDataAction = createObservableDataAction({
    id: 'mmx/get-available-inventory-nearby',
    action: <IAction<OrgUnitAvailability[]>>getAvailableInventoryNearbyAction,
    input: createGetAvailableInventoryNearbyInput
});

export default getAvailableInventoryNearbyActionDataAction;
